import useSEO from "../hooks/useSEO";
import { CheckCircle2, Leaf, Wrench, HeartHandshake } from "lucide-react";

export default function About() {
  useSEO({
    title: "Über uns · Caravan Reisen aus Neusalza-Spremberg",
    description: "Caravan Reisen wurde von Wieland Neumann gegründet und vermietet moderne Reisemobile aus Neusalza-Spremberg in Sachsen. Persönliche Beratung, gepflegte Fahrzeuge und faire Preise.",
    path: "/ueber-uns",
  });

  return (
    <div className="pt-32 pb-16 max-w-5xl mx-auto px-6 lg:px-10" data-testid="about-page">
      <span className="chip">Über uns</span>
      <h1 className="section-title mt-4 text-5xl md:text-6xl max-w-3xl">Reisen mit Freiheit im Gepäck.</h1>

      <p className="mt-6 text-lg text-[#1B2A22]/75 leading-relaxed max-w-3xl">
        Caravan Reisen ist ein familiengeführtes Vermietungsunternehmen aus dem sächsischen Neusalza-Spremberg.
        Wir helfen dir dabei, dein individuelles Roadtrip-Abenteuer zu planen – ob spontaner Wochenendtrip nach
        Tschechien, entspannter Familienurlaub an der Ostsee oder monatelange Europareise. Bei uns findest du
        das passende Reisemobil, ehrliche Beratung und einen Service, der über den reinen Fahrzeug­schlüssel hinausgeht.
      </p>

      <div className="grid md:grid-cols-2 gap-12 mt-16 items-center">
        <img
          src="https://images.pexels.com/photos/8973334/pexels-photo-8973334.jpeg?auto=compress&w=1400"
          alt="Reisemobil-Camping Sonnenuntergang"
          className="rounded-2xl aspect-[4/5] object-cover"
        />
        <div className="space-y-6 text-[#1B2A22]/80 leading-relaxed">
          <h2 className="font-display font-bold text-3xl text-[#1B2A22]">Unsere Geschichte</h2>
          <p>
            Angefangen hat alles mit einem einzigen gebrauchten Kastenwagen und der festen Überzeugung, dass Urlaub
            nicht kompliziert sein muss. Wieland Neumann, selbst begeisterter Camper, gründete Caravan Reisen mit
            dem Ziel, Menschen unkomplizierten Zugang zu qualitativ hochwertigen Reisemobilen zu geben – ohne
            Verkaufsdruck, ohne endlose Papierstapel und ohne versteckte Kosten.
          </p>
          <p>
            Heute betreuen wir Kunden aus ganz Deutschland und dem europäischen Ausland. Jedes unserer Fahrzeuge
            wird persönlich betreut, regelmäßig gewartet und mit Liebe zum Detail vorbereitet. Wir kennen jedes
            Reisemobil in unserer Flotte in- und auswendig und können deshalb ehrlich einschätzen, welches Modell
            zu deinen Plänen passt.
          </p>
          <p>
            Nachhaltigkeit liegt uns am Herzen: Wir setzen auf verbrauchsarme Motoren, digitale Prozesse statt
            gedruckter Formulare und langlebige Ausstattung, die viele Reisen mitmacht. Unsere Werkstatt vor Ort
            hält jedes Fahrzeug jahrelang im Top-Zustand.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20">
        {[
          { n: "1.200+", l: "Zufriedene Reisende" },
          { n: "4", l: "Reisemobile in der Flotte" },
          { n: "4.9/5", l: "Bewertung Google" },
          { n: "24/7", l: "Pannenhilfe" },
        ].map((s, i) => (
          <div key={i} className="p-6 bg-white rounded-2xl border border-[#D8D6D0]">
            <div className="font-display font-bold text-3xl text-[#C05C42]">{s.n}</div>
            <div className="text-sm text-[#1B2A22]/70 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-24">
        <h2 className="font-display font-bold text-3xl md:text-4xl">Warum Caravan Reisen?</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {[
            {
              icon: Wrench,
              title: "Eigene Werkstatt in Neusalza-Spremberg",
              text: "Alle Reisemobile werden bei uns vor Ort geprüft, gereinigt und gewartet. Kein anonymer Massen-Vermieter, sondern echte Handarbeit.",
            },
            {
              icon: HeartHandshake,
              title: "Persönliche Beratung",
              text: "Du sprichst direkt mit dem Inhaber. Wir nehmen uns Zeit für dich und beraten ehrlich – auch wenn es bedeutet, dir ein günstigeres Fahrzeug zu empfehlen.",
            },
            {
              icon: Leaf,
              title: "Ehrliche Preise",
              text: "Keine versteckten Aufschläge, keine überraschenden Reinigungspauschalen. Was du siehst, ist was du zahlst.",
            },
            {
              icon: CheckCircle2,
              title: "Rundum-Sorglos-Paket",
              text: "Vollkasko, Pannenhilfe und Servicehotline sind bereits im Mietpreis enthalten. Du musst nur einsteigen und losfahren.",
            },
          ].map((f) => (
            <div key={f.title} className="p-8 bg-white rounded-2xl border border-[#D8D6D0]">
              <f.icon size={24} className="text-[#C05C42]" />
              <h3 className="mt-5 font-display font-semibold text-xl">{f.title}</h3>
              <p className="mt-2 text-[#1B2A22]/75 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-24 rounded-3xl bg-[#F0EFEA] p-10 md:p-14 border border-[#D8D6D0]">
        <h2 className="font-display font-bold text-3xl md:text-4xl">Wo du uns findest</h2>
        <p className="mt-4 text-[#1B2A22]/75 leading-relaxed max-w-2xl">
          Unser Standort in Neusalza-Spremberg liegt verkehrsgünstig zwischen Dresden, Bautzen und der tschechischen
          Grenze – perfekt für den Start Richtung Böhmen, Sächsische Schweiz oder Ostsee. Übernahme und Rückgabe
          erfolgen montags bis freitags von 9:00 bis 17:00 Uhr nach vorheriger Terminvereinbarung.
        </p>
        <p className="mt-4 font-semibold">
          Caravan Reisen<br/>
          Taubenheimer Weg 3, 02742 Neusalza-Spremberg, Deutschland
        </p>
      </div>
    </div>
  );
}
